import './BaselineCalendar.css';

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {format} from 'date-fns';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {Link} from 'react-router-dom';

import {apiService} from '../../../apiService';
import {useAuth} from '../../../contexts/AuthContext';
import {generateDateRange, useFilterDates} from '../../../utils';
import {DateRangeFilter, EmployeeNameCell} from '../../common';

const DEFAULT_DAYS = 5;

const StatusCell = ({value}) => {
  if (value === null) {
    return <div className='status-cell striped' />;
  }

  return (
    <div className={`status-cell ${value ? 'yes' : 'no'}`}>
      {value ? 'Yes' : 'No'}
    </div>
  );
};

export const BaselineCalendar = ({orgId}) => {
  const {currentUser} = useAuth();
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);
  const filteredData = useMemo(
    () =>
      data.filter((row) =>
        row.name.toLowerCase().includes(search.toLowerCase()),
      ),
    [data, search],
  );

  const {date, setDate} = useFilterDates(DEFAULT_DAYS);

  const dates = useMemo(
    () => generateDateRange(date.startDate, date.endDate),
    [date],
  );

  const columns = useMemo(
    () => [
      {
        header: null,
        accessorKey: 'name',
        id: 'employee',
        cell: (info) => (
          <EmployeeNameCell
            name={info.getValue()}
            badge={info.row.original.baselineCompleted ? 'Completed' : null}
          />
        ),
      },
      ...dates.map((date) => {
        const key = format(date, 'yyyy-MM-dd');
        return {
          header: () => (
            <div className='date-header'>{format(date, 'EEE MM/dd/yy')}</div>
          ),
          id: `day_${key}`,
          columns: [
            {
              header: 'Pre',
              id: `pre_${key}`,
              accessorFn: (row) => row.days[key]?.pre,
              cell: (info) => <StatusCell value={info.getValue()} />,
            },
            {
              header: 'Post',
              id: `post_${key}`,
              accessorFn: (row) => row.days[key]?.post,
              cell: (info) => <StatusCell value={info.getValue()} />,
            },
          ],
        };
      }),
    ],
    [dates],
  );

  const table = useReactTable({
    data: filteredData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchData = useCallback(async () => {
    if (!orgId || !currentUser.uid) {
      return;
    }
    let url = '/api/get-manager-recordings-summary?';
    url += `&uid=${currentUser.uid}&`;
    url += `&start_date=${format(date.startDate, 'yyyy-MM-dd')}`;
    url += `&end_date=${format(date.endDate, 'yyyy-MM-dd')}`;

    const response = await apiService.get(url);
    let employeesData = [];

    if (response?.body && Array.isArray(response.body)) {
      const orgData = response.body.find((org) => org.id === orgId);
      employeesData = orgData?.employees || [];
    }

    const sortedEmployees = [...employeesData].sort((a, b) => {
      const firstNameCompare = a.first_name.localeCompare(b.first_name);
      if (firstNameCompare !== 0) return firstNameCompare;
      return a.last_name.localeCompare(b.last_name);
    });

    const transformedData = sortedEmployees.map((emp) => {
      const daysMap = {};

      dates.forEach((date) => {
        const key = format(date, 'yyyy-MM-dd');

        const dayRecordings = emp.recordings.filter((rec) => rec.date === key);
        const baselineCompletedAt = emp.baseline_completed_at;
        const completed = baselineCompletedAt && key > baselineCompletedAt;

        if (dayRecordings.length === 0) {
          daysMap[key] = {
            pre: completed ? null : false,
            post: completed ? null : false,
          };
        } else {
          const hasPreShift = dayRecordings.some((rec) => rec.pre_shift);
          const hasPostShift = dayRecordings.some((rec) => rec.post_shift);

          daysMap[key] = {
            pre: completed ? null : hasPreShift,
            post: completed ? null : hasPostShift,
          };
        }
      });

      return {
        id: emp.id,
        name: `${emp.first_name} ${emp.last_name}`,
        days: daysMap,
        baselineCompleted: !!emp.baseline_completed_at,
      };
    });

    setData(transformedData);
  }, [orgId, currentUser.uid, date.startDate, date.endDate, dates]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!orgId || !currentUser.uid) {
    return null;
  }

  return (
    <div className='calendar-wrapper'>
      <h3 className='calendar-title'>Calendar</h3>

      <div className='calendar-toolbar'>
        <DateRangeFilter
          onApply={setDate}
          maxRangeDays={14}
          currentRangeDays={DEFAULT_DAYS}
        />
        <input
          className='search'
          placeholder='Search employee...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className='table-scroll'>
        <table className='calendar-table'>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id} colSpan={header.colSpan}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className='calendar-legend'>
        <span className='legend-striped' />
        <div>
          Baseline completed. Model activated. For compliance and impairment
          information, visit the{' '}
          <Link to='/dashboard/impairment'>Impairment page</Link>
        </div>
      </div>
    </div>
  );
};
